import { useTranslation } from '@mypoz/i18n';
import { Icon, Modal } from '@mypoz/ui';

import { buttonClass } from '@/lib/styles.js';

export interface TopUpAbandonedDialogProps {
  /**
   * Whether `get_trade` was reached and said the top-up is unpaid. False when the check got no
   * answer: then the dialog cannot say it was not paid, only that it could not tell.
   */
  checked: boolean;
  /** Closes the dialog; the card then puts the cursor back in the amount box. */
  onClose: () => void;
}

/**
 * "Payment not completed" (MYP-134): shown once when the customer comes back to the Top up card
 * from the payment page — the browser's Back button — without having paid.
 *
 * Pressing Pay already recorded the top-up with the vendor (INVALID, unpaid). Nothing in the app
 * can cancel it, and the vendor's guide says its link must not be reused, so this top-up is over
 * and a new one has to be started. Paying again is the one risk: a customer who did pay at the bank
 * while the vendor is still crediting it would pay twice. So the text never says no money was
 * taken, and both versions send the customer to the top-up history first when in doubt — the
 * unchecked one (the look-up got no answer) above all.
 */
export function TopUpAbandonedDialog({ checked, onClose }: TopUpAbandonedDialogProps) {
  const { t } = useTranslation();
  const key = checked ? 'wallet.topUp.abandoned' : 'wallet.topUp.abandonedUnchecked';

  return (
    <Modal
      title={t(`${key}.title`)}
      onClose={onClose}
      closeLabel={t('actions.close')}
      icon={
        <span className="flex size-9 items-center justify-center rounded-full bg-warning/15 text-warning">
          <Icon name="priority_high" size={20} />
        </span>
      }
    >
      <div className="flex flex-col gap-3 text-sm leading-relaxed">
        <p className="text-ink">{t(`${key}.body`)}</p>
        <p className="font-semibold text-ink">{t(`${key}.again`)}</p>
        <p className="rounded-md bg-surface-alt p-3 text-xs text-ink">{t(`${key}.note`)}</p>
      </div>
      <div className="mt-5 flex sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          className={buttonClass('primary', 'w-full sm:w-auto')}
        >
          {t(`${key}.action`)}
        </button>
      </div>
    </Modal>
  );
}
